import React, { useCallback, useEffect, useState } from 'react';
import { Container, Body2, TrashIcon, EditIcon, Body1Light, Flex } from '@/ui';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminKYCApi } from '@/api';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { COLORS } from '@/utils/static';
import { Divider, Modal } from 'antd';
import { Button, Input, Loading } from '@/components';
import { usePopups } from '@/hooks';

export const NotesModal = ({ active, onClose, questionId, questionTitle, refetchQuest }) => {
  const { questionnaireId } = useParams();
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = usePopups();
  const { data: dataNotes, refetch: refetchNotes } = useQuery({
    queryKey: ['getQuestionnaireNotes'],
    queryFn: () =>
      adminKYCApi.getQuestionnaireNotes({
        investorQuestionSectionTypeId: +questionnaireId,
        questionId
      }),
    enabled: active,
    onSuccess: () => {
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    }
  });
  const { mutateAsync: addNote } = useMutation({ mutationFn: adminKYCApi.addQuestionnaireNote });
  const { mutateAsync: deleteNote } = useMutation({
    mutationFn: adminKYCApi.deleteQuestionnaireNote
  });
  const { mutateAsync: updateNote } = useMutation({
    mutationFn: adminKYCApi.updateQuestionnaireNote
  });
  const [note, setNote] = useState(null);
  const [newNote, setNewNote] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [needToRefetch, setNeedToRefetch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const onAdd = async () => {
    setIsDisabledButton(true);
    try {
      const result = await addNote({
        investorQuestionSectionTypeId: +questionnaireId,
        questionId,
        note
      });

      if (result) {
        refetchNotes();
        showSuccessToast('Note added successfully');
        setNote(null);
        setIsDisabledButton(false);
        setNeedToRefetch(true);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
      setIsDisabledButton(false);
    }
  };

  const onUpdate = async (noteId) => {
    try {
      const result = await updateNote({
        noteId,
        newNote
      });

      if (result) {
        refetchNotes();
        showSuccessToast('Note updated successfully');
        setNewNote(null);
        setEditingNoteId(null);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  const onDelete = async (noteId) => {
    try {
      const result = await deleteNote({
        noteId: +noteId
      });

      if (result) {
        refetchNotes();
        showSuccessToast('Note has been deleted');
        setNeedToRefetch(true);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  const handleEditButton = (noteId) => {
    if (!editingNoteId) {
      setEditingNoteId(noteId);
    } else {
      setEditingNoteId(null);
      setNewNote(null);
    }
  };

  const closeModal = useCallback(() => {
    if (needToRefetch) refetchQuest();
    onClose();
    setNeedToRefetch(false);
    setNote(null);
    setNewNote(null);
    setIsDisabledButton(false);
    setEditingNoteId(null);
    setTimeout(() => {
      setIsLoading(true);
    }, 1000);
  }, [needToRefetch, refetchQuest]);

  useEffect(() => {
    if (active) setIsLoading(true);
  }, [active]);

  return (
    <Modal open={active} onCancel={closeModal} footer={null}>
      <Flex vertical gap={3}>
        <Body2 $fontWeight={500}>{t('Notes for question')}</Body2>
        <Body2>'{questionTitle}':</Body2>
      </Flex>

      {isLoading ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        <>
          {dataNotes && dataNotes.data && dataNotes.data.length > 0 ? (
            <Flex $marginTop={20} $marginBottom={30} gap={8}>
              {dataNotes.data.map((note) => (
                <Note key={note.id}>
                  {editingNoteId !== note.id ? (
                    <Body2>{note.note}</Body2>
                  ) : (
                    <Flex gap={8} align='center'>
                      <Input
                        defaultValue={note.note}
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                      />
                      <Button text={t('Change')} block={false} onClick={() => onUpdate(note.id)} />
                    </Flex>
                  )}
                  <Flex gap={8}>
                    <EditIcon onClick={() => handleEditButton(note.id)} />
                    <TrashIcon onClick={() => onDelete(note.id)} />
                  </Flex>
                </Note>
              ))}
            </Flex>
          ) : (
            <Flex justify='center' align='center' $marginTop={20}>
              <Body1Light $color={COLORS.greyMedium}>{t('No notes yet')}.</Body1Light>
            </Flex>
          )}
        </>
      )}

      <Divider />
      <Container>
        <Input label={t('Add note')} value={note} onChange={(e) => setNote(e.target.value)} />
      </Container>

      <Flex justify='center' $marginTop={28}>
        <Button text={t('Add')} onClick={onAdd} disabled={isDisabledButton} type='primary' />
      </Flex>
    </Modal>
  );
};

const LoadingWrapper = styled.div`
  width: 100%;
  height: 100px;
  position: relative;
  margin-top: 10px;
`;

const Note = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: ${COLORS.greyLightBasic};
`;
